/**
 * EventReport — full post-event report shown on EventDashboard's "Report" tab.
 *
 * Tab visibility is gated to events whose event_date is in the past — this
 * component handles ALL rendering once the parent decides to show it.
 *
 * Pulls /api/events/{event_id}/report and renders:
 *  - Headline KPIs (sold, gross, net, refunds, attendance, page views)
 *  - AI coaching paragraph (Claude)
 *  - Ticket sales by tier
 *  - Revenue breakdown
 *  - Hourly/daily sales curve
 *  - Check-in by tier
 *  - Page views + sources + widget hosts
 *  - Share & Track presets
 *  - Production summary (tasks / budget / vendors / staff)
 *  - Marketing summary (email / SMS / syndication / promo codes)
 *  - Promo partner leaderboard + commissions
 *  - Survey response + NPS + top testimonial
 *  - Service audit checklist (used vs unused) with next-time recommendations
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, CheckCircle2, Circle, BarChart3, Ticket, DollarSign, RefreshCcw,
  Users, Eye, Megaphone, Wrench, Handshake, Star, Sparkles, ArrowRight,
  TrendingUp, ChevronRight,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { readableApiError } from '../utils/apiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token') || ''}` } });

const fmtUSD = (n) => `$${(Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtNum = (n) => Number(n || 0).toLocaleString();
const pct = (v) => v == null ? '—' : `${v}%`;


function KpiTile({ icon: Icon, label, value, sub, color, testId }) {
  return (
    <div
      data-testid={testId}
      className="p-4 rounded-2xl border border-border bg-card flex items-start gap-3"
    >
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${color}/10 ${color}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
        <p className="text-xl font-extrabold tabular-nums mt-0.5">{value}</p>
        {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}


function Section({ icon: Icon, title, children, testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-5 space-y-3">
        <h3 className="font-bold text-sm flex items-center gap-2 uppercase tracking-wider text-muted-foreground">
          {Icon && <Icon className="h-4 w-4" />} {title}
        </h3>
        {children}
      </CardContent>
    </Card>
  );
}


export default function EventReport({ eventId }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/events/${eventId}/report`, auth());
        if (!cancelled) setReport(r.data);
      } catch (err) {
        toast.error(readableApiError(err) || 'Failed to load report');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId]);

  if (loading) {
    return (
      <div data-testid="event-report-loading" className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Building your report…
      </div>
    );
  }
  if (!report) return null;

  const ev = report.event;
  const s = report.sections;
  const orgName = ev.organization_name || 'team';

  return (
    <div data-testid="event-report" className="space-y-5">
      {/* ===== Header / Thank-you ===== */}
      <Card className="overflow-hidden border-0">
        <div className="relative p-6 md:p-8 text-white"
             style={{ background: 'linear-gradient(135deg,#8b5cf6 0%,#6366f1 50%,#0ea5e9 100%)' }}>
          <div className="text-[10px] uppercase tracking-widest opacity-80 font-semibold">Post-Event Report</div>
          <h2 className="text-2xl md:text-3xl font-extrabold mt-1" data-testid="report-event-name">{ev.name}</h2>
          <div className="text-sm opacity-90 mt-1">
            {ev.event_date}{ev.venue_name ? ` · ${ev.venue_name}` : ''}{ev.city ? ` · ${ev.city.charAt(0).toUpperCase() + ev.city.slice(1)}` : ''}
          </div>
          <div className="mt-4 text-[13px] opacity-95 leading-relaxed max-w-xl">
            Thank you, <strong>{orgName}</strong>, for choosing Eventism to sell tickets to {ev.name}.
            Here&apos;s the full performance breakdown
            {ev.campaign_length_days ? ` from the day you created the draft ${ev.campaign_length_days} days ago` : ''}
            {' '}through doors-close. Drill into any section below.
          </div>
        </div>
      </Card>

      {/* ===== KPIs ===== */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3" data-testid="report-kpis">
        <KpiTile icon={Ticket} label="Tickets Sold"
                 value={fmtNum(s.ticket_sales.total_sold)}
                 sub={s.ticket_sales.sell_through_pct != null ? `${s.ticket_sales.sell_through_pct}% sell-through` : ''}
                 color="text-indigo-300" testId="report-kpi-sold" />
        <KpiTile icon={DollarSign} label="Gross Revenue"
                 value={fmtUSD(s.revenue.gross)} color="text-sky-300" testId="report-kpi-gross" />
        <KpiTile icon={TrendingUp} label="Net Revenue"
                 value={fmtUSD(s.revenue.net)} sub={`AOV ${fmtUSD(s.revenue.average_order_value)}`}
                 color="text-emerald-300" testId="report-kpi-net" />
        <KpiTile icon={RefreshCcw} label="Refunds"
                 value={fmtUSD(s.revenue.refunds)} sub={`${s.revenue.refund_count} orders`}
                 color="text-rose-300" testId="report-kpi-refunds" />
        <KpiTile icon={Users} label="Check-in"
                 value={pct(s.checkin.attendance_pct)}
                 sub={`${fmtNum(s.checkin.total_checked_in)} / ${fmtNum(s.checkin.total_issued)}`}
                 color="text-amber-300" testId="report-kpi-checkin" />
        <KpiTile icon={Eye} label="Page Views"
                 value={fmtNum(s.page_views.total_views)}
                 sub={`${fmtNum(s.page_views.unique_visitors)} unique`}
                 color="text-fuchsia-300" testId="report-kpi-views" />
      </div>

      {/* ===== AI Coaching ===== */}
      {s.ai_coaching && (
        <Card data-testid="report-ai-coaching" className="border-amber-200/50">
          <CardContent className="p-5 space-y-3 bg-gradient-to-br from-amber-500/5 via-transparent to-fuchsia-500/5">
            <h3 className="font-bold text-sm flex items-center gap-2 uppercase tracking-wider text-amber-300">
              <Sparkles className="h-4 w-4" /> From your Eventism coach
            </h3>
            {s.ai_coaching.split('\n').filter(p => p.trim()).map((para, i) => (
              <p key={i} className="text-[14px] leading-relaxed text-foreground/90">{para}</p>
            ))}
          </CardContent>
        </Card>
      )}

      {/* ===== Ticket sales by tier ===== */}
      <Section icon={Ticket} title="Ticket sales by tier" testId="report-tier-section">
        {(s.ticket_sales.by_tier || []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No ticket tiers were configured for this event.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="report-tier-table">
              <thead><tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
                <th className="text-left py-2 pr-3">Tier</th>
                <th className="text-right py-2 px-3">Price</th>
                <th className="text-right py-2 px-3">Sold / Cap</th>
                <th className="text-right py-2 px-3">Sell-through</th>
                <th className="text-right py-2 pl-3">Revenue</th>
              </tr></thead>
              <tbody>
                {s.ticket_sales.by_tier.map((t) => (
                  <tr key={t.ticket_type_id} className="border-t border-border" data-testid={`report-tier-row-${t.ticket_type_id}`}>
                    <td className="py-2 pr-3 font-medium">{t.name}</td>
                    <td className="py-2 px-3 text-right tabular-nums">{fmtUSD(t.price)}</td>
                    <td className="py-2 px-3 text-right tabular-nums">{t.sold} / {t.capacity || '∞'}</td>
                    <td className="py-2 px-3 text-right tabular-nums">{pct(t.sell_through_pct)}</td>
                    <td className="py-2 pl-3 text-right tabular-nums font-semibold">{fmtUSD(t.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {s.ticket_sales.top_tier && (
          <p className="text-xs text-muted-foreground">
            <strong className="text-foreground">Top tier:</strong> {s.ticket_sales.top_tier}
          </p>
        )}
      </Section>

      {/* ===== Revenue breakdown ===== */}
      <Section icon={DollarSign} title="Revenue breakdown" testId="report-revenue-section">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div className="space-y-2">
            <RevenueRow label="Gross revenue" value={fmtUSD(s.revenue.gross)} />
            <RevenueRow label="Platform fees" value={`– ${fmtUSD(s.revenue.fees)}`} dim />
            <RevenueRow label={`Refunds (${s.revenue.refund_count})`} value={`– ${fmtUSD(s.revenue.refunds)}`} dim />
            <RevenueRow label="Net revenue" value={fmtUSD(s.revenue.net)} accent />
          </div>
          <div className="space-y-2">
            <RevenueRow label="Orders completed" value={fmtNum(s.revenue.orders_completed)} />
            <RevenueRow label="Orders created" value={fmtNum(s.revenue.orders_created)} />
            <RevenueRow label="Checkout completion" value={pct(s.revenue.checkout_completion_pct)} />
            <RevenueRow label="Average order value" value={fmtUSD(s.revenue.average_order_value)} />
          </div>
        </div>
      </Section>

      {/* ===== Sales curve (hourly/daily) ===== */}
      <Section icon={TrendingUp} title="Sales velocity" testId="report-curve-section">
        {(s.hourly_curve || []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No completed orders to chart yet.</p>
        ) : (
          <SalesCurve data={s.hourly_curve} />
        )}
      </Section>

      {/* ===== Check-in by tier ===== */}
      <Section icon={Users} title="Check-in breakdown" testId="report-checkin-section">
        <div className="grid grid-cols-3 gap-3 text-center">
          <StatBox label="Issued" value={fmtNum(s.checkin.total_issued)} />
          <StatBox label="Checked in" value={fmtNum(s.checkin.total_checked_in)} accent="text-emerald-300" />
          <StatBox label="Attendance" value={pct(s.checkin.attendance_pct)} accent="text-amber-300" />
        </div>
        {(s.checkin.by_tier || []).length > 0 && (
          <div className="overflow-x-auto pt-2">
            <table className="w-full text-sm">
              <thead><tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
                <th className="text-left py-2 pr-3">Tier</th>
                <th className="text-right py-2 px-3">Issued</th>
                <th className="text-right py-2 px-3">Checked in</th>
                <th className="text-right py-2 pl-3">Attendance</th>
              </tr></thead>
              <tbody>
                {s.checkin.by_tier.map((t, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="py-2 pr-3 font-medium">{t.name}</td>
                    <td className="py-2 px-3 text-right tabular-nums">{t.issued}</td>
                    <td className="py-2 px-3 text-right tabular-nums">{t.checked_in}</td>
                    <td className="py-2 pl-3 text-right tabular-nums">{pct(t.attendance_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* ===== Page views + sources + widget hosts ===== */}
      <Section icon={Eye} title="Page views &amp; traffic sources" testId="report-traffic-section">
        <div className="grid grid-cols-3 gap-3 text-center">
          <StatBox label="Total views" value={fmtNum(s.page_views.total_views)} />
          <StatBox label="Unique visitors" value={fmtNum(s.page_views.unique_visitors)} />
          <StatBox label="Conv. rate"
                   value={pct(s.revenue.checkout_completion_pct)}
                   accent="text-indigo-300" />
        </div>
        <div className="grid md:grid-cols-2 gap-4 pt-3">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Top sources</p>
            {(s.page_views.sources || []).length === 0 ? (
              <p className="text-xs text-muted-foreground">No tracked sources.</p>
            ) : (
              <table className="w-full text-sm" data-testid="report-sources-table">
                <tbody>
                  {s.page_views.sources.map((r, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="py-1.5 pr-2">{r.source}</td>
                      <td className="py-1.5 text-right tabular-nums">{fmtNum(r.views)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Embed widget hosts</p>
            {(s.page_views.widget_hosts || []).length === 0 ? (
              <p className="text-xs text-muted-foreground">No partners embedded your widget.</p>
            ) : (
              <table className="w-full text-sm" data-testid="report-widget-table">
                <thead><tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="text-left py-1">Host</th>
                  <th className="text-right py-1">Impr.</th>
                  <th className="text-right py-1">Conv.</th>
                  <th className="text-right py-1">Rev.</th>
                </tr></thead>
                <tbody>
                  {s.page_views.widget_hosts.map((r, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="py-1.5 pr-2 truncate max-w-[160px]">{r.host}</td>
                      <td className="py-1.5 text-right tabular-nums">{r.impressions}</td>
                      <td className="py-1.5 text-right tabular-nums">{r.conversions}</td>
                      <td className="py-1.5 text-right tabular-nums">{fmtUSD(r.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Share presets */}
        {(s.share_presets?.presets || []).length > 0 && (
          <div className="pt-2">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">
              Share &amp; Track presets — {fmtNum(s.share_presets.total_clicks)} clicks · {fmtUSD(s.share_presets.total_revenue)}
            </p>
            <table className="w-full text-sm">
              <tbody>
                {s.share_presets.presets.map((p, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="py-1.5 pr-2">{p.label}</td>
                    <td className="py-1.5 text-right tabular-nums">{p.clicks} clicks</td>
                    <td className="py-1.5 text-right tabular-nums">{fmtUSD(p.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* ===== Production summary ===== */}
      <Section icon={Wrench} title="Production summary" testId="report-production-section">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
          <StatBox label="Tasks done" value={`${s.production.tasks.done} / ${s.production.tasks.total}`}
                   accent="text-emerald-300" />
          <StatBox label="Budget actual"
                   value={fmtUSD(s.production.budget.actual)}
                   sub={`vs ${fmtUSD(s.production.budget.planned)} plan`}
                   accent={s.production.budget.variance > 0 ? 'text-rose-300' : 'text-emerald-300'} />
          <StatBox label="Vendors confirmed"
                   value={`${s.production.vendors.confirmed} / ${s.production.vendors.total}`}
                   accent="text-indigo-300" />
          <StatBox label="Staff filled"
                   value={`${s.production.staff.filled} / ${s.production.staff.total}`}
                   accent="text-amber-300" />
        </div>
      </Section>

      {/* ===== Marketing summary ===== */}
      <Section icon={Megaphone} title="Marketing summary" testId="report-marketing-section">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
          <StatBox label="Email campaigns" value={fmtNum(s.marketing.email_campaigns.count)}
                   sub={`${fmtNum(s.marketing.email_campaigns.sent)} sent, ${pct(s.marketing.email_campaigns.open_rate_pct)} open`}
                   accent="text-indigo-300" />
          <StatBox label="SMS blasts" value={fmtNum(s.marketing.sms_blasts.count)}
                   sub={`${fmtNum(s.marketing.sms_blasts.sent)} sent`}
                   accent="text-fuchsia-300" />
          <StatBox label="Syndications" value={fmtNum(s.marketing.syndications_count)} accent="text-sky-300" />
          <StatBox label="Promo codes" value={fmtNum(s.marketing.promo_codes.count)}
                   sub={`${fmtNum(s.marketing.promo_codes.total_redemptions)} redemptions`}
                   accent="text-amber-300" />
        </div>
      </Section>

      {/* ===== Promo partners ===== */}
      <Section icon={Handshake} title="Promo partner leaderboard" testId="report-partners-section">
        {(s.promo_partners.top_partners || []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No promo partner activity this campaign.</p>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
              <th className="text-left py-2 pr-3">Partner</th>
              <th className="text-right py-2 px-3">Tickets</th>
              <th className="text-right py-2 px-3">Revenue</th>
              <th className="text-right py-2 pl-3">Commission</th>
            </tr></thead>
            <tbody>
              {s.promo_partners.top_partners.map((p, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="py-2 pr-3 truncate max-w-[220px]">
                    {p.partner_name || p.partner_email || p.tracking_code || 'Partner'}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums">{p.tickets_sold}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{fmtUSD(p.revenue)}</td>
                  <td className="py-2 pl-3 text-right tabular-nums">{fmtUSD(p.commission)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="text-xs text-muted-foreground">
          Total commissions paid: <strong className="text-foreground">{fmtUSD(s.promo_partners.total_commissions_paid)}</strong>
        </p>
      </Section>

      {/* ===== Surveys ===== */}
      <Section icon={Star} title="Buyer feedback &amp; NPS" testId="report-surveys-section">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
          <StatBox label="Surveys sent" value={fmtNum(s.surveys.sent)} />
          <StatBox label="Response rate" value={pct(s.surveys.response_rate_pct)} accent="text-indigo-300" />
          <StatBox label="Avg rating" value={s.surveys.avg_rating ?? '—'} accent="text-amber-300" />
          <StatBox label="NPS"
                   value={s.surveys.nps == null ? '—' : s.surveys.nps}
                   sub={s.surveys.nps != null ? `${s.surveys.promoters}p / ${s.surveys.passives}n / ${s.surveys.detractors}d` : ''}
                   accent="text-emerald-300" />
        </div>
        {s.surveys.top_testimonial && (
          <blockquote className="border-l-4 border-amber-400 pl-4 italic text-sm text-foreground/90 mt-3"
                      data-testid="report-top-testimonial">
            &ldquo;{s.surveys.top_testimonial.comment}&rdquo;
            <footer className="not-italic text-xs text-muted-foreground mt-1">
              — {s.surveys.top_testimonial.buyer_name || 'Anonymous buyer'}
              {s.surveys.top_testimonial.rating ? ` · ${s.surveys.top_testimonial.rating}/5` : ''}
            </footer>
          </blockquote>
        )}
      </Section>

      {/* ===== Service audit ===== */}
      <Section icon={CheckCircle2} title="Service audit — what you used &amp; what's next" testId="report-audit-section">
        <div className="space-y-2">
          {(s.service_audit || []).map((svc, i) => (
            <ServiceRow key={svc.service} svc={svc} />
          ))}
        </div>
      </Section>

      <div className="text-[11px] text-muted-foreground text-center pt-2">
        Report generated {new Date(report.generated_at).toLocaleString()} · Eventism
      </div>
    </div>
  );
}


function RevenueRow({ label, value, dim, accent }) {
  return (
    <div className="flex items-center justify-between border-b border-border/60 py-1.5">
      <span className={dim ? 'text-muted-foreground' : ''}>{label}</span>
      <span className={`tabular-nums font-semibold ${accent ? 'text-emerald-300 text-base' : ''}`}>{value}</span>
    </div>
  );
}


function StatBox({ label, value, sub, accent }) {
  return (
    <div className="rounded-lg bg-muted/40 border border-border p-3">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
      <p className={`text-lg font-extrabold mt-0.5 tabular-nums ${accent || ''}`}>{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}


function SalesCurve({ data }) {
  const max = Math.max(1, ...data.map(d => d.orders));
  return (
    <div data-testid="report-sales-curve" className="space-y-1.5">
      {data.map((d) => (
        <div key={d.date} className="flex items-center gap-3 text-xs">
          <span className="w-20 text-muted-foreground tabular-nums">{d.date}</span>
          <div className="flex-1 h-5 bg-muted/40 rounded relative overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-indigo-400 to-fuchsia-400 rounded"
                 style={{ width: `${Math.max(4, (d.orders / max) * 100)}%` }} />
          </div>
          <span className="w-16 text-right tabular-nums font-semibold">{d.orders} ord</span>
          <span className="w-20 text-right tabular-nums text-muted-foreground">${d.revenue.toFixed(0)}</span>
        </div>
      ))}
    </div>
  );
}


function ServiceRow({ svc }) {
  const used = svc.used;
  return (
    <div data-testid={`report-audit-${svc.service}`}
         className={`p-3 rounded-lg border flex items-start gap-3 ${used ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-amber-500/30 bg-amber-500/5'}`}>
      {used ? <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" /> : <Circle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold">{svc.label}
          <Badge variant="secondary" className={`ml-2 text-[10px] ${used ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}`}>
            {used ? 'Used' : 'Not used'}
          </Badge>
        </p>
        <p className="text-xs text-muted-foreground mt-1">{svc.why_matters}</p>
        {!used && (
          <p className="text-xs mt-2 text-foreground/90"><strong>Try next time:</strong> {svc.recommendation}</p>
        )}
        {svc.deep_link && (
          <a href={svc.deep_link} className="inline-flex items-center text-xs font-semibold mt-2 text-indigo-300 hover:text-indigo-200">
            {used ? 'Review setup' : 'Set up now'} <ChevronRight className="h-3 w-3 ml-0.5" />
          </a>
        )}
      </div>
    </div>
  );
}
